import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { FiCalendar, FiFolder, FiPlus, FiTrash } from "react-icons/fi";
import propTypes from "prop-types";

import DropDownField, { IDropDownListItem } from "../../editable/EditableField/fields/DropDownField";
import MoneyInputField from "../../editable/EditableField/fields/MoneyInputField";
import NumberInputField from "../../editable/EditableField/fields/NumberInputField";
import TextInputField from "../../editable/EditableField/fields/TextInputField";
import DateField from "../../editable/EditableField/fields/DateField";
import SwitchField from "../../editable/EditableField/fields/SwitchField";

import { api, Package, PackagedProduct, ProductRate, ProductTypes } from "../../../logic/api";

import flex from "../../../styles/flex";

import ProductPickerDialog from "../../pickers/productPicker/ProductPickerDialog";

import ErrorDialog from "../../notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../notifications/progressDialog/progressDialog";

import { formatCurrency } from "../../../utility/utils";
import { ReleaseFlag, useReleaseManagement } from "../../../utility/feature-management";
import { SessionContext } from "../../../utility/useSession";
import { useDate, isValidDate } from "../../../utility/useDate";
import { useTranslation } from "react-i18next";

export enum PackageAvailability {
  available = 0,
  NotYetAvailable = 1,
  NoLongerAvailable = 2,
  SoldOut = 3
}

export const calculatePackagePrice = (products: PackagedProduct[]) => {
  let normalPrice = 0;
  let dealPrice = 0;
  let normalPriceIncVat = 0;
  let dealPriceIncVat = 0;

  let amountTemp = 0;

  if (products != null) {
    for (const p of products) {
      if (p.product != null) {
        if (p.id != -1) {

          if (p.rateType == ProductRate.standard) {
            amountTemp = (p.product.stdRate * p.qty);
          } else if (p.rateType == ProductRate.adjusted) {
            amountTemp = (p.rate * p.qty);
          } else {
            amountTemp = 0;
          }

          dealPriceIncVat += amountTemp;
          dealPrice += (amountTemp - (amountTemp * (p.product.vat / 100)));// deduct the tax.


          amountTemp = (p.product.stdRate * p.qty);

          normalPriceIncVat += amountTemp;
          normalPrice += (amountTemp - (amountTemp * (p.product.vat / 100)));// deduct the tax.
        }
      }
    }
  }

  return {
    normalPrice,
    normalPriceIncVat,
    dealPrice,
    dealPriceIncVat
  }
}


const PackageBuilder: React.FunctionComponent<{
  packageId: number | null,
  show: boolean;
  onClose: (didUpdate: boolean) => void;
}> = (props) => {
  const session = useContext(SessionContext);
  const { localizeDate } = useDate();
  const fetchPackage = api.hooks.packages.get();
  const savePackage = api.hooks.packages.save();
  const { t } = useTranslation();

  let walkInRate = 0;
  let carParkId = 1;
  let defaultTaxRate = 0;

  if (session.current.site) {
    carParkId = session.current.site.home.id;
    walkInRate = session.current.site.preferences.walkin_rate;
    defaultTaxRate = session.current.site.preferences.vat_rate;
  }

  const defaultStartDate = new Date();
  const localDefaultStartDate = localizeDate(defaultStartDate).toISO();

  const [showDialog, setShowDialog] = useState(false);

  const [packageId, setPackageId] = useState<number | null>(null);
  const [packagedProducts, setPackagedProducts] = useState<PackagedProduct[]>([]);
  const [packageName, setPackageName] = useState("");
  const [packageCode, setPackageCode] = useState("");
  const [packageRef, setPackageRef] = useState("");

  const [startDate, setStartDate] = useState(localDefaultStartDate);
  const [endDate, setEndDate] = useState("");
  const [expires, setOfferExpires] = useState(false);
  const [active, setActive] = useState(true);
  const [preBookOnly, setPreBookOnly] = useState(false);
  const [pickingOffset, setPickingOffset] = useState<number | null>(null);

  const [err, setErr] = useState<Error | null>(null);

  const isAdditionalTaxRatesReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalTaxRates);

  const buildWalkinProduct = (rate: number, rateType: ProductRate) => {
    const product: PackagedProduct = {
      id: -1,
      product: {
        carParkId: carParkId,
        id: -1,
        name: t("packages.walkIn"),
        active: true,
        productType: ProductTypes.walkIn,
        code: "D",
        description: t("packages.rateMultiplied"),
        parentId: 0,
        rootId: null,
        hasChildren: false,
        stdRate: walkInRate,
        costOfSale: 0,
        maxQty: 0,
        maxQtyEnabled: false,
        maxQtyPerDay: 0,
        maxQtyPerDayEnabled: false,
        vat: defaultTaxRate
      },
      qty: 1,
      rate: rate,
      rateType: rateType,
    }
    return product;
  };

  useEffect(() => {
    const propPackageId = props.packageId;
    if (propPackageId == null) {
      setPackageId(propPackageId);
      setPackageName("");
      setPackageCode("");
      setPackageRef("");
      setStartDate(localDefaultStartDate);
      setEndDate("");
      setActive(true);
      setPreBookOnly(false);
      setOfferExpires(false);
      setPackagedProducts([buildWalkinProduct(walkInRate, ProductRate.standard)]);
      setShowDialog(true);
    } else {
      setPackageId(propPackageId);
      setPackageName("");
      fetchPackage.send(session.current.context, {
        id: propPackageId
      });
    }
  }, [props.packageId, props.show]);


  useEffect(() => {
    if (fetchPackage.data != null) {
      setPackageName(fetchPackage.data.name);
      setStartDate(fetchPackage.data.startDate == null ? "" : localizeDate(new Date(fetchPackage.data.startDate)).toISO() ?? "");
      setEndDate(fetchPackage.data.endDate == null ? "" : localizeDate(new Date(fetchPackage.data.endDate)).toISO() ?? "");
      setActive(fetchPackage.data.active);
      setPreBookOnly(fetchPackage.data.preBookOnly);
      setOfferExpires(fetchPackage.data.expires == true);
      setPackageCode((fetchPackage.data.code || "").toString());
      setPackageRef((fetchPackage.data.ref || "").toString());
      setPackagedProducts([buildWalkinProduct(fetchPackage.data.walkinRate, fetchPackage.data.walkinRateType), ...fetchPackage.data.products]);
      setPickingOffset(fetchPackage.data.pickingOffset || 0);

      setShowDialog(true);
    }
  }, [fetchPackage.data]);

  const generatePackageCode = () => {
    let code = "";
    const addCode = (cd: string) => {
      cd = cd == null ? "" : cd;
      if (cd.length > 0) {
        if (code.length > 0) {
          code += "-";
        }
        code += cd;
      }
    }
    if (packageId != null) {
      code += (packageId == null ? "" : (packageId).toString(16).padStart(2, "0")).toUpperCase();
    } else {
      code = "#";
    }
    const pkgProducts = [...packagedProducts];

    const processCode = (productType: ProductTypes) => {
      for (let i = 0; i < pkgProducts.length; i++) {
        if (pkgProducts[i].product) {
          if (pkgProducts[i].product.productType == productType) {
            addCode(pkgProducts[i].product.code);
            pkgProducts.splice(i, 1);
            break;
          }
        }
      }
    }

    processCode(ProductTypes.valet);
    processCode(ProductTypes.collection);
    processCode(ProductTypes.delivery);
    processCode(ProductTypes.valet);
    processCode(ProductTypes.electricVehicle);
    processCode(ProductTypes.vip);

    return code;
  }

  useEffect(() => {
    setPackageCode(generatePackageCode());
  }, [packagedProducts]);



  const [showProductPickerDialog, setShowProductPickerDialog] = useState(false);

  const removePackagedProduct = (packagedProduct: PackagedProduct) => {
    const indx = packagedProducts.indexOf(packagedProduct);
    if (indx != -1) {
      const prods = [...packagedProducts];
      prods.splice(indx, 1);
      setPackagedProducts(prods);
    }
  }

  const save = async () => {
    const prods = packagedProducts.filter((p) => {
      return p.id != -1 && p.product != null;
    });
    const walkIn = packagedProducts.find((p) => {
      return p.id == -1;
    });
    if (walkIn == null) {
      setErr(new Error(t("packages.noWalkInProd")));
      return;
    }

    const inActiveProducts = prods.find((p) => {
      return p.product.active != true;
    });
    if (inActiveProducts != null) {
      setErr(new Error(t("packages.inactiveProds")));
      return;
    }
    const pkgCode = generatePackageCode();
    setPackageCode(pkgCode);

    const pkg: Package = {
      id: packageId,
      name: packageName,
      code: pkgCode,
      ref: packageRef,
      startDate: (startDate ?? "").toString().split("T")[0],
      endDate: isValidDate(endDate) ? endDate.split('T')[0] : null,
      products: prods,
      walkinRateType: walkIn.rateType,
      walkinRate: walkIn.rateType == ProductRate.adjusted ? walkIn.rate : walkInRate,
      expires: expires,
      active: active,
      preBookOnly: preBookOnly,
      pickingOffset: pickingOffset ?? undefined,
    }

    savePackage.send(session.current.context, pkg, (result) => {
      if (result != null) {
        props.onClose(true);
      }
    });

  }

  const price = calculatePackagePrice(packagedProducts);

  return (
    <Fragment>
      <ProgressDialog status={savePackage.status} delay={500}></ProgressDialog>
      <ProgressDialog status={fetchPackage.status} showOnIdle={props.show == true && showDialog == false} delay={500}></ProgressDialog>
      <ErrorDialog err={fetchPackage.err} onClose={() => {
        props.onClose(false);
      }}></ErrorDialog>


      <Dialog
        open={props.show == true && showDialog == true}
        fullWidth={true}
        maxWidth={"lg"}

      >
        <DialogTitle>
          <b>{packageName.length == 0 ? t("packages.builder") : packageName}</b>
        </DialogTitle>

        <DialogContent style={{ minHeight: 60 }}>
          <ErrorDialog err={savePackage.err}></ErrorDialog>
          <ErrorDialog err={err}></ErrorDialog>
          <ProductPickerDialog title={t("packages.addProduct")} show={showProductPickerDialog} onClose={(product) => {
            setShowProductPickerDialog(false);
            if (product != null) {
              const prods = [...packagedProducts];

              const existingProduct = prods.find((p) => {
                if (p.id == -1) {
                  return false;
                }
                if (p.product == null) {
                  return false;
                }
                //Match products by their root product id.
                return p.product.rootId == product.rootId;
              });

              if (existingProduct != null) {
                if (existingProduct.product.id == product.id) {
                  setErr(new Error(t("packages.packageContainsProduct", { name: product.name })));
                  return;
                } else {
                  if (existingProduct.product.productType != ProductTypes.default) {
                    setErr(new Error(t("packages.similarProducts", { name: product.name, existing: existingProduct.product.name })));
                    return;
                  }
                }
              }

              prods.push({
                product: product,
                qty: 1,
                rate: product.stdRate,
                rateType: ProductRate.standard,
              });

              setPackagedProducts(prods);
            }
          }}></ProductPickerDialog>
          <div>
            <div style={flex.flexRows}>
              <div style={{ ...flex.flexAuto }}>
                <div style={{ backgroundColor: "#eee", width: 200 }}>
                  <div style={flex.flexRows}>
                    <div style={flex.flexFill}>
                      <div style={{ padding: 12, textAlign: "left", width: 180 }}>
                        <b>{t("packages.options")}</b>
                      </div>
                    </div>
                    <div style={flex.flexAuto}>
                      <div style={{ visibility: "hidden" }}>
                        <IconButton>
                          <FiFolder />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={flex.flexFill}>
                <div style={{ backgroundColor: "#eee" }}>
                  <div style={flex.flexRows}>
                    <div style={flex.flexFill}>
                      <div style={{ padding: 12, textAlign: "left" }}>
                        <b>{t("booking.labels.products")}</b>
                      </div>
                    </div>
                    <div style={flex.flexAuto}>
                      <IconButton
                        onClick={() => {
                          setShowProductPickerDialog(true);
                        }}
                      >
                        <FiPlus />
                      </IconButton>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </div>
        </DialogContent>

        <DialogContent >
          <div style={{ minHeight: "50vh" }}>

            <div style={flex.flexRows}>
              <div style={{ ...flex.flexAuto }}>
                <div style={{ width: 200 }}>
                  <div style={flex.flexRows}>
                    <div style={flex.flexFill}>
                      <div style={{ paddingRight: 10 }}>
                        <div style={{ marginBottom: 50 }}>
                          <TextInputField label={t("packages.name")} value={packageName} onChange={(val) => {
                            setPackageName(val);
                          }} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <TextInputField readOnly={true} label={t("packages.code")} value={packageCode} onChange={(val) => {
                            setPackageCode(val);
                          }} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <TextInputField label={t("packages.thirdParty")} value={packageRef} onChange={(val) => {
                            setPackageRef(val);
                          }} />
                        </div>
                        <div style={{ marginBottom: 15 }}>
                          <NumberInputField label={t("packages.pickingOffset")} value={pickingOffset as number} onChange={(val) => {
                            setPickingOffset(Math.max(val, 0) || null);
                          }} />
                        </div>
                        <div style={{ marginBottom: 10 }}>
                          <SwitchField label={t("packages.packageActive")} value={active} onChange={(val) => {
                            setActive(val == true);
                          }} />
                        </div>
                        <div style={{ marginBottom: 50 }}>
                          <SwitchField label={t("packages.prebookOnly")} value={preBookOnly} onChange={(val) => {
                            setPreBookOnly(val == true);
                          }} />
                        </div>
                        <div style={{ marginBottom: 15 }}>
                          <DateField label={t("packages.availableFrom")} value={startDate} onChange={(val) => {
                            setStartDate(val);
                          }} />
                        </div>
                        {expires == true && (
                          <div style={{ marginBottom: 15 }}>
                            <DateField label={t("packages.availableUntil")} value={endDate} onChange={(val) => {
                              setEndDate(val);
                            }} />
                          </div>
                        )}

                        <div style={{ marginBottom: 15 }}>

                          <SwitchField label={t("packages.availabilityExpires")} value={expires} onChange={(val) => {
                            setOfferExpires(val == true);
                          }} />
                        </div>

                      </div>

                    </div>

                  </div>
                </div>
              </div>
              <div style={flex.flexFill}>
                <div>
                  {packagedProducts.map((packagedProduct, index) => {
                    if (packagedProduct.product == null) {
                      return <Fragment key={index}></Fragment>;
                    }
                    return (
                      <div key={index} style={{
                        borderBottom: "solid 1px lightgray",
                        borderLeft: "solid 1px lightgray",
                        borderRight: "solid 1px lightgray",
                        borderTop: index == 0 ? "solid 1px lightgray" : "none",
                        borderTopLeftRadius: index == 0 ? 5 : 0,
                        borderTopRightRadius: index == 0 ? 5 : 0,
                        borderBottomLeftRadius: index == packagedProducts.length - 1 ? 5 : 0,
                        borderBottomRightRadius: index == packagedProducts.length - 1 ? 5 : 0,
                        padding: 5
                      }}>
                        <div style={flex.flexRows}>
                          <div style={flex.flexAuto}>
                            <div style={{ paddingRight: 10 }}>
                              <div>
                                {packagedProduct.id != -1 && (
                                  <IconButton
                                    onClick={() => {
                                      removePackagedProduct(packagedProduct);
                                    }}
                                  >
                                    <FiTrash />
                                  </IconButton>
                                )}
                                {packagedProduct.id == -1 && (
                                  <IconButton disabled={true}>
                                    <FiCalendar />
                                  </IconButton>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={flex.flexFill}>
                            <div style={{ paddingTop: 5 }}>
                              <div>
                                <b>{packagedProduct.product.name}</b> {packagedProduct.product.active != true && (<b style={{ color: "red" }}>
                                  {t("packages.productNotActive")}
                                </b>)}
                              </div>
                              <div>
                                <span>{packagedProduct.product.description}</span>
                              </div>
                            </div>
                          </div>
                          {packagedProduct.product.maxQty != 0 && (
                            <div style={flex.flexAuto}>
                              <div style={{ paddingTop: 5, paddingRight: 5 }}>
                                <NumberInputField maxWidth={70} label={"Qty"} value={packagedProduct.qty} onChange={(value) => {
                                  if (value <= 0) {
                                    value = 1;
                                  }
                                  const prods = [...packagedProducts];
                                  prods[index] = { ...packagedProduct };
                                  prods[index].qty = value;
                                  setPackagedProducts(prods);

                                }}></NumberInputField>
                              </div>
                            </div>
                          )}
                          <div style={flex.flexAuto}>
                            <div style={{ paddingTop: 5, paddingRight: 5 }}>
                              <DropDownField
                                label={t("packages.rateType")}
                                minWidth={150}
                                selectedItem={{
                                  value: packagedProduct.rateType.toString()
                                } as IDropDownListItem}
                                onRequestData={async () => {
                                  const items = [
                                    { "name": t("packages.standard"), "value": ProductRate.standard.toString() },
                                    { "name": t("packages.adjusted"), "value": ProductRate.adjusted.toString() },
                                  ];
                                  if (packagedProduct.id != -1) {
                                    items.push({ "name": t("packages.complimentary"), "value": ProductRate.compDiscount.toString() });
                                  }
                                  return items;
                                }}
                                onChange={(selectedItem) => {
                                  const prods = [...packagedProducts];
                                  prods[index] = { ...packagedProduct };
                                  prods[index].rateType = parseInt(selectedItem.value || "");
                                  setPackagedProducts(prods);
                                }}></DropDownField>
                            </div>
                          </div>

                          <div style={flex.flexAuto}>
                            <div style={{ paddingTop: 5, paddingRight: 5 }}>
                              <MoneyInputField readOnly={packagedProduct.rateType != ProductRate.adjusted}
                                maxWidth={200}
                                label={t("packages.rateIncTax", { tax: !isAdditionalTaxRatesReleased ? ' @ ' + formatCurrency(packagedProduct.product.vat, session) : '' })}
                                value={
                                  packagedProduct.rateType == ProductRate.compDiscount ?
                                    0 : (packagedProduct.rateType == ProductRate.standard ?
                                      parseFloat(packagedProduct.product.stdRate.toString()) : parseFloat(packagedProduct.rate.toString()))}
                                onChange={(value) => {
                                  const prods = [...packagedProducts];
                                  prods[index] = { ...packagedProduct };
                                  prods[index].rate = value;
                                  setPackagedProducts(prods);
                                }}></MoneyInputField>
                            </div>
                          </div>


                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>


        <DialogContent style={{ minHeight: 60, backgroundColor: "white" }}>
          <div style={flex.flexRows}>
            <div style={flex.flexFill}></div>
            <div style={flex.flexAuto}>
              <div style={flex.flexRows}>
                <div style={{ textAlign: "right", width: 200, paddingRight: 10 }}>
                  <span>{t("packages.packagePrice")}:</span>
                </div>
                <div style={flex.flexFill}>
                  {formatCurrency(price.dealPrice, session)} <small>(Ex Tax)</small> &nbsp;/&nbsp; <b>{formatCurrency(price.dealPriceIncVat, session)} <small>{t("packages.incTax")}</small></b>
                </div>
              </div>

              <div style={flex.flexRows}>
                <div style={{ textAlign: "right", width: 200, paddingRight: 10 }}>
                  <span>{t("packages.standardPrice")}:</span>
                </div>
                <div style={flex.flexFill}>
                  {formatCurrency(price.normalPrice, session)} <small>{t("packages.exTax")}</small> &nbsp;/&nbsp; <b>{formatCurrency(price.normalPriceIncVat, session)} <small>{t("packages.incTax")}</small></b>
                </div>
              </div>


            </div>
            <div style={flex.flexFill}></div>
          </div>

        </DialogContent>

        <DialogActions>
          <Button onClick={() => {
            props.onClose(false);
          }}>{t("general.cancel")}</Button>
          <Button disabled={packageName.length == 0} onClick={() => save()}><b>{t("packages.savePackage")}</b></Button>
        </DialogActions>

      </Dialog >



    </Fragment >
  );
}
PackageBuilder.propTypes = {
  packageId: propTypes.any,
  show: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
}
export default PackageBuilder;
